import { useEffect, useRef } from 'react';

interface GeometricOverlayProps {
  opacity?: number;
}

export default function GeometricOverlay({ opacity = 0.8 }: GeometricOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let shapes: Shape[] = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();

    // Parse CSS color string (hex or rgb/rgba) to [r,g,b]
    const parseCssColorToRgb = (input: string): [number, number, number] => {
      let c = input.trim();
      if (!c) return [10, 116, 255];
      const rgbMatch = c.match(/^rgba?\(([^)]+)\)$/i);
      if (rgbMatch) {
        const parts = rgbMatch[1].split(',').map(v => parseFloat(v.trim()));
        return [
          Math.max(0, Math.min(255, parts[0] || 0)),
          Math.max(0, Math.min(255, parts[1] || 0)),
          Math.max(0, Math.min(255, parts[2] || 0)),
        ];
      }
      if (c.startsWith('#')) c = c.slice(1);
      if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
      if (c.length !== 6) return [10, 116, 255];
      const r = parseInt(c.slice(0,2), 16);
      const g = parseInt(c.slice(2,4), 16);
      const b = parseInt(c.slice(4,6), 16);
      return [r,g,b];
    };

    // Accent rgba base string updated each frame
    let colorBase = 'rgba(10, 116, 255,';

    class Shape {
      x: number;
      y: number;
      size: number;
      vx: number;
      vy: number;
      rotation: number;
      rotationSpeed: number;
      type: number;
      opacity: number;
      color: string;

      constructor() {
        this.x = 0;
        this.y = 0;
        this.size = 0;
        this.vx = 0;
        this.vy = 0;
        this.rotation = 0;
        this.rotationSpeed = 0;
        this.type = 0;
        this.opacity = 0;
        this.color = '';
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 50 + 20;
        this.vx = (Math.random() - 0.5) * 0.3;
        this.vy = (Math.random() - 0.5) * 0.3;
        this.rotation = Math.random() * Math.PI * 2;
        this.rotationSpeed = (Math.random() - 0.5) * 0.02;
        this.type = Math.floor(Math.random() * 3);
        this.opacity = Math.random() * 0.3 + 0.1;
        // Use theme accent color for stroke (updated live each frame)
        this.color = colorBase;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.rotation += this.rotationSpeed;

        if (this.x < -this.size) this.x = canvas.width + this.size;
        if (this.x > canvas.width + this.size) this.x = -this.size;
        if (this.y < -this.size) this.y = canvas.height + this.size;
        if (this.y > canvas.height + this.size) this.y = -this.size;
      }

      draw() {
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);
        ctx.strokeStyle = `${colorBase} ${this.opacity})`;
        ctx.lineWidth = 2;

        if (this.type === 0) {
          ctx.beginPath();
          ctx.arc(0, 0, this.size / 2, 0, Math.PI * 2);
          ctx.stroke();
        } else if (this.type === 1) {
          ctx.strokeRect(-this.size / 2, -this.size / 2, this.size, this.size);
        } else {
          ctx.beginPath();
          ctx.moveTo(0, -this.size / 2);
          ctx.lineTo(this.size / 2, this.size / 2);
          ctx.lineTo(-this.size / 2, this.size / 2);
          ctx.closePath();
          ctx.stroke();
        }

        ctx.restore();
      }
    }

    const createShapes = () => {
      shapes = [];
      const shapeCount = 30;
      for (let i = 0; i < shapeCount; i++) {
        shapes.push(new Shape());
      }
    };

    createShapes();

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Read header accent each frame to respond to theme/accent changes
      const accent = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-header-accent')
        .trim() || '#0A74FF';
      const [r,g,b] = parseCssColorToRgb(accent);
      colorBase = `rgba(${r}, ${g}, ${b},`;

      shapes.forEach(shape => {
        shape.update();
        shape.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      resizeCanvas();
      createShapes();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [opacity]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: -1,
        opacity: opacity
      }}
    />
  );
}
